import React, { useRef } from 'react';
import { FieldValues, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import cn from 'classnames';

import { Guideline, Label } from '@components/common';

import { ERadioColors, TRadioColors } from '@enums/colors';

import styles from './RadioButton.module.scss';

interface IRadioButtonProps {
	checked?: boolean;
	defaultChecked?: boolean;
	disabled?: boolean;
	id: string;
	isLabelBefore?: boolean;
	label: string;
	name: string;
	onChange?: (value: any) => void;
	setValue?: UseFormSetValue<FieldValues>;
	register?: UseFormRegister<FieldValues>;
	withUseForms?: boolean;
	color?: TRadioColors;
	infoTooltip?: string | null;
}

const RadioButton = ({
	checked,
	defaultChecked,
	disabled = false,
	id,
	isLabelBefore = false,
	label,
	name,
	onChange,
	setValue,
	register,
	withUseForms = false,
	color,
	infoTooltip,
}: IRadioButtonProps) => {
	const labelRef = useRef<HTMLLabelElement | null>(null);

	const handleLabelClick = () => labelRef?.current && labelRef.current.click();

	const handleChange = () => {
		if (onChange) onChange(id);
		if (setValue) setValue(name, id);
	};

	const paletteClasses = {
		[styles[ERadioColors.red]]: color === ERadioColors.red,
		[styles[ERadioColors.amber]]: color === ERadioColors.amber,
		[styles[ERadioColors.green]]: color === ERadioColors.green,
	};

	return (
		<div className={styles.container}>
			{isLabelBefore && (
				<Label
					isFieldLabel={false}
					label={label}
					onClick={handleLabelClick}
				/>
			)}
			<label
				ref={labelRef}
				className={cn(styles.radioButton, {
					...paletteClasses,
					[styles.disabled]: disabled,
				})}
				htmlFor={id}
			>
				{withUseForms
					? <input
						aria-label={name}
						checked={checked}
						defaultChecked={defaultChecked}
						disabled={disabled}
						id={id}
						type='radio'
						{...register ? register(name) : {}}
						onChange={handleChange}
					/>
					: <input
						aria-label={name}
						checked={checked}
						defaultChecked={defaultChecked}
						disabled={disabled}
						id={id}
						name={name}
						onChange={handleChange}
						type='radio'
					/>
				}
				<span className={cn(styles.checked, paletteClasses)} />
			</label>
			{!isLabelBefore && (
				<Label
					isFieldLabel={false}
					label={label}
					onClick={handleLabelClick}
				/>
			)}
			{infoTooltip && (
				<Guideline
					guidelineText={infoTooltip}
					placement='top'
				/>
			)}
		</div>
	);
};

export default RadioButton;
